<template>
  <div class="view-container">
    <div class="extended-filter-block">
      <h2>HR Администраторы</h2>

      <div class="extended-search-block-mobile">
        <div class="search-outline-block">
          <SearchOutlined
              @click="showMobileFilterModal = true"
              style="color: rgba(0, 0, 0, 0.45)"
          />

          <div v-if="filterString" class="dot"></div>
        </div>
      </div>
    </div>

    <div class="card card__with-action">
      <div class="filter-block">
        <div>
          <a-segmented
              v-model:value="limitFilter"
              :options="limitFilterData"
          />
        </div>

        <div class="search-tool-block">
          <a-select
              v-model:value="additionalFilters.groups"
              class="groups-filter-select"
              mode="multiple"
              placeholder="Выберите группы"
              :not-found-content="'Нет данных'"
              :loading="groupsLoading"
              :show-arrow="true"
              @change="handleGroupSelect"
              @dropdownVisibleChange="groupsSelectOpen = $event"
          >
            <template #suffixIcon>
              <UpOutlined v-if="groupsSelectOpen" />
              <DownOutlined v-else />
            </template>

            <a-select-option
                v-for="group in groups"
                :key="group.id"
                :value="group.id"
            >
              {{ group.name }}
            </a-select-option>
          </a-select>
        </div>

        <div
            v-if="!getRight('hr_department')"
            class="add-button"
        >
          <a-button @click="addNewUser" class="primary-button">
            <template #icon>
              <PlusOutlined />
            </template>

            Добавить
          </a-button>
        </div>
      </div>

      <TableHorizontalScrollShell ref="tableShell">
        <a-table
            ref="tableRef"
            rowKey="id"
            class="admins-table"
            :columns="columns"
            :data-source="userData"
            :scroll="tableScroll"
            :row-selection="{ selectedRowKeys: selectedUsers, onChange: onSelectChange, columnWidth: 40 }"
            :pagination="false"
            :loading="tableLoading"
            :table-layout="'fixed'"
            @change="handleTableChange"
        >
          <template #bodyCell="{ column, text, record }">
            <template v-if="column.dataIndex === 'name'">
              <a
                  class="staff-link"
                  @click="router.push(`/staff/${record.id}/edit`)"
              >
                {{ text }}
              </a>
            </template>

            <template v-if="column.dataIndex === 'job_title'">
              <span>{{ text || '' }}</span>
            </template>

            <template v-if="column.dataIndex === 'role'">
              <span>{{ text || '' }}</span>
            </template>

            <template v-if="column.dataIndex === 'action'">
              <span
                  class="change-role-text"
                  @click="handleAction('change_role', record)"
              >
                Настроить лимиты
              </span>
            </template>

            <template v-if="column.dataIndex === 'groups'">
              <div
                  v-if="Array.isArray(record.groups) && record.groups.length"
                  class="staff-groups-cell"
              >
                <a-tag
                    v-for="group in getVisibleGroups(record.groups)"
                    :key="group.id"
                    class="staff-group-tag"
                >
                  {{ group.name }}
                </a-tag>

                <a-popover
                    v-if="getHiddenGroups(record.groups).length"
                    trigger="hover"
                    placement="bottom"
                >
                  <template #content>
                    <div class="staff-groups-popover">
                      <div
                          v-for="group in getHiddenGroups(record.groups)"
                          :key="group.id"
                          class="staff-groups-popover__item"
                      >
                        {{ group.name }}
                      </div>
                    </div>
                  </template>

                  <a-tag class="staff-group-tag staff-group-tag--more">
                    +{{ getHiddenGroups(record.groups).length }}
                  </a-tag>
                </a-popover>
              </div>
            </template>

            <template v-if="column.dataIndex === 'settings'">
              <a-dropdown
                  :trigger="['click']"
                  placement="bottomLeft"
              >
                <a-button
                    type="text"
                    @click.prevent
                >
                  <MenuOutlined />
                </a-button>

                <template #overlay>
                  <a-menu @click="({ key }) => handleAction(key, record)">
                    <a-menu-item key="change_role">
                      Настроить лимиты
                    </a-menu-item>
                  </a-menu>
                </template>
              </a-dropdown>
            </template>
          </template>

          <template #emptyText>
            <div class="no-data-wrapper">
              <img
                  :src="NotFoundIcon"
                  class="no-data-image"
                  alt="Нет результатов"
              />

              <div class="no-data-text">
                {{
                  filterString
                      ? 'Нет данных для отображения. Измените фильтры или запрос.'
                      : 'Нет данных для отображения. Добавьте сотрудников, чтобы начать работу.'
                }}
              </div>

              <a-button
                  v-if="filterString"
                  @click="resetFilters"
              >
                <template #icon>
                  <ReloadOutlined />
                </template>

                Сбросить фильтры
              </a-button>

              <a-button
                  v-else
                  @click="addNewUser"
              >
                <template #icon>
                  <PlusOutlined />
                </template>

                Добавить
              </a-button>
            </div>
          </template>
        </a-table>
      </TableHorizontalScrollShell>

      <div class="bottom-container">
        <a-pagination
            v-if="!hidePagination"
            v-model:current="currentPage"
            :page-size="entitiesPerPage"
            :buildOptionText="count => `${count.value} / стр`"
            :show-total="isMobile ? undefined : (total => `Всего ${total ?? 0} элементов`)"
            :total="totalUsers"
            :show-less-items="isMobile"
            @change="handlePageChange"
        />
      </div>
    </div>

    <UsersFilterMobileForm
        v-if="showMobileFilterModal"
        :open="showMobileFilterModal"
        :groups="groups"
        @close="showMobileFilterModal = false"
    />

    <SyncGroupsForm
        v-if="currentAction === 'groups'"
        :open="currentAction === 'groups'"
        :group-list="groups"
        :user_groups="currentActionUser.groups"
        :user_id="currentActionUser.id"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentUserAndAction"
    />

    <CopyUsersIntoGroup
        v-if="currentAction === 'copy_to_group'"
        :open="currentAction === 'copy_to_group'"
        :group-list="groups"
        :users="selectedUsers"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentUserAndAction"
    />

    <SendInviteForm
        v-if="currentAction === 'send_invite'"
        :open="currentAction === 'send_invite'"
        :currentActionUser="currentActionUser"
        :selectedUsers="selectedUsers"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentUserAndAction"
    />

    <BlockUserForm
        v-if="currentAction === 'block' || currentAction === 'unblock'"
        :open="currentAction === 'block' || currentAction === 'unblock'"
        :type="currentAction"
        :currentActionUser="currentActionUser"
        :selectedUsers="selectedUsers"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentUserAndAction"
    />

    <ResetPasswordForm
        v-if="currentAction === 'reset_password'"
        :open="currentAction === 'reset_password'"
        :currentActionUser="currentActionUser"
        :selectedUsers="selectedUsers"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentUserAndAction"
    />

    <AddHRAdmin
        v-if="currentAction === 'add_user'"
        :open="currentAction === 'add_user'"
        @close="clearCurrentUserAndAction"
        @submitted="fetchAfterSubmit"
    />

    <NewOrderForm
        v-if="showOrderForm"
        :open="showOrderForm"
        @close="showOrderForm = false"
    />

    <ManageAccessForm
        v-if="currentAction === 'manage_access'"
        :open="currentAction === 'manage_access'"
        :userData="currentActionUser?.id ? [currentActionUser.id] : selectedUsers"
        :name="currentActionUser?.id ? currentActionUser.name : null"
        :mode="'staff'"
        @close="clearCurrentUserAndAction"
        @submitted="clearCurrentUserAndAction"
        @showStaff="() => {
          selectedUsers = [];
          clearCurrentUserAndAction();
          resetFilters();
        }"
        @showOrderForm="() => {
          clearCurrentUserAndAction();
          showOrderForm = true;
        }"
    />

    <ChangeStaffRole
        v-if="currentAction === 'change_role'"
        :open="currentAction === 'change_role'"
        :role="currentActionUser.role ?? ''"
        :userId="currentActionUser.id ?? null"
        :user="currentActionModalUser"
        :managingGroups="currentActionUser.groups ?? []"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentUserAndAction"
    />
  </div>
</template>

<script>
import {
  computed,
  onBeforeMount,
  reactive,
  ref,
  watch,
} from 'vue'
import { useStore } from 'vuex'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import {
  SearchOutlined,
  SettingOutlined,
  PlusOutlined,
  ReloadOutlined,
  MenuOutlined,
  UpOutlined,
  DownOutlined,
} from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'

import NotFoundIcon from '@/assets/images/icons/no_data.svg'
import NewStaffForm from '@/views/Staff/NewStaffForm.vue'
import ManageAccessForm from '@/components/Forms/Access/ManageAccessForm.vue'
import NewOrderForm from '@/views/Orders/OrderForm.vue'
import ResetPasswordForm from '@/feature/users/forms/ResetPasswordForm.vue'
import BlockUserForm from '@/feature/users/forms/BlockUserForm.vue'
import SendInviteForm from '@/feature/users/forms/SendInviteForm.vue'
import CopyUsersIntoGroup from '@/feature/group-control/forms/CopyUsersIntoGroup.vue'
import SyncGroupsForm from '@/feature/group-control/forms/SyncGroupsForm.vue'
import UsersFilterMobileForm from '@/feature/users/UsersFilterMobileForm.vue'
import TableHorizontalScrollShell from '@/components/Tables/TableScrollComponent.vue'

import { useTableColumns } from '@/mixins/useTableColumns'
import useCheckRights from '@/mixins/useCheckRights'
import ChangeStaffRole from '@/feature/users/ChangeStaffRole.vue'
import rolesTranslate from '@/mixins/dictionaries/rolesTranslate'
import AddHRAdmin from '@/feature/users/forms/AddHRAdmin.vue'
import useBreakpoints from '@/mixins/useBreakpoints'
import { setSpecialStaffFromRoute } from '@/utils/specialStaffRoute'

export default {
  name: 'staff-with-limits-page',

  components: {
    AddHRAdmin,
    ChangeStaffRole,
    TableHorizontalScrollShell,
    UsersFilterMobileForm,
    SyncGroupsForm,
    CopyUsersIntoGroup,
    SendInviteForm,
    BlockUserForm,
    ResetPasswordForm,
    NewOrderForm,
    ManageAccessForm,
    NewStaffForm,
    SearchOutlined,
    SettingOutlined,
    PlusOutlined,
    ReloadOutlined,
    MenuOutlined,
    UpOutlined,
    DownOutlined,
  },

  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()

    setSpecialStaffFromRoute(store, route)

    const { getRight } = useCheckRights()

    const normalizeGroup = (group) => {
      return {
        id: group?.id ?? group?.group_id ?? null,
        name: group?.name ?? group?.group_name ?? '',
      }
    }

    const normalizeGroups = (items = []) => {
      if (!Array.isArray(items)) {
        return []
      }

      return items
          .map(normalizeGroup)
          .filter((group) => group.id)
    }

    const mergeGroupsIntoList = (items = []) => {
      normalizeGroups(items).forEach((group) => {
        const exists = groups.value.some((existingGroup) => {
          return Number(existingGroup.id) === Number(group.id)
        })

        if (!exists) {
          groups.value.push(group)
        }
      })
    }

    const showOrderForm = ref(false)
    const applyToAll = ref(false)
    const actionToApply = ref()
    const tableLoading = ref(true)
    const groupsLoading = ref(true)
    const showMobileFilterModal = ref(false)
    const showActionModal = ref(false)
    const modalActionLoading = ref(false)
    const groupsSelectOpen = ref(false)

    const limitFilterData = reactive(['Все', 'Без лимитов'])
    const limitFilter = ref(limitFilterData[0])

    const allGroups = computed(() => store.state.group.groups)
    const groups = ref([])

    const filters = computed(() => store.state.users.filters)
    const filterString = computed(() => store.state.users.filterString)

    const additionalFilters = reactive({
      groups: filters.value?.groups ?? [],
      without_limits_only: filters.value?.without_limits_only ?? false,
    })

    const selectedUsers = ref([])
    const users = computed(() => store.state.users.users)

    const userData = computed(() => {
      return users.value.map((staff) => {
        return {
          id: staff.id,
          user_id: staff.user_id ?? staff.id,
          name: staff.name,
          job_title: staff?.position_name ?? '',
          role: staff?.role ? rolesTranslate[staff.role] : '',
          original_role: staff.role,
          status: staff?.is_active ? 'active' : 'not_active',
          groups: normalizeGroups(staff.responsibility),
        }
      })
    })

    const currentActionUser = ref({
      id: null,
      user_id: null,
      name: '',
      groups: [],
      role: null,
    })

    const currentActionModalUser = computed(() => {
      if (!currentActionUser.value?.user_id) {
        return null
      }

      return {
        user_id: currentActionUser.value.user_id,
        name: currentActionUser.value.name,
      }
    })

    const currentAction = ref('')
    const showFilterModal = ref(false)

    const entitiesPerPage = ref(10)
    const currentPage = ref(1)
    const totalUsers = computed(() => store.state.users.totalUsers)
    const specialStaff = computed(() => store.state.users.specialStaff)
    const hidePagination = ref(false)

    const filtersPopoverOpen = ref(false)

    const initialColumns = ref([
      {
        title: 'ФИО',
        dataIndex: 'name',
        sorter: true,
        visible: true,
        width: 260,
      },
      {
        title: 'Должность',
        dataIndex: 'job_title',
        sorter: true,
        visible: true,
        width: 190,
      },
      {
        title: 'Роль',
        dataIndex: 'role',
        sorter: true,
        visible: true,
        width: 200,
      },
      {
        title: 'Действие',
        dataIndex: 'action',
        visible: true,
        width: 190,
      },
      {
        title: 'Группы',
        dataIndex: 'groups',
        visible: true,
        width: 250,
      },
    ])

    const {
      columns,
      scrollX,
      tableRef,
      tableScroll,
      updateTableYScroll,
    } = useTableColumns(initialColumns, {
      tableKey: 'staff_limits',
      settingsWidth: 40,
      selectionWidth: 40,
      dataSource: userData,
    })

    const sortState = ref({
      field: null,
      order: null,
    })

    const fetchUsers = () => {
      tableLoading.value = true

      store.dispatch('users/getAdminStaff', {
        special_staff: specialStaff.value?.length ? specialStaff.value : null,
        per_page: entitiesPerPage.value,
        page: currentPage.value,
        sort_field: sortState.value.field,
        sort_direction:
            sortState.value.order === 'ascend'
                ? 'asc'
                : sortState.value.order === 'descend'
                    ? 'desc'
                    : null,
        ...filters.value,
      }).then(() => {
        if (specialStaff.value?.length) {
          hidePagination.value = true
          store.dispatch('users/clearSpecialStaff')
        } else if (hidePagination.value) {
          hidePagination.value = false
        }

        tableLoading.value = false
      }).catch(() => {
        tableLoading.value = false
      })
    }

    const onSelectChange = (selectedRowKeys) => {
      selectedUsers.value = selectedRowKeys
    }

    const addNewUser = () => {
      currentAction.value = 'add_user'
      showActionModal.value = true
    }

    const getGroupsVisibleLimit = (recordGroups = []) => {
      if (!Array.isArray(recordGroups)) {
        return 0
      }

      const firstThreeGroups = recordGroups.slice(0, 3)

      const hasLongGroupName = firstThreeGroups.some((group) => {
        return String(group?.name ?? '').length > 18
      })

      return hasLongGroupName ? 2 : 3
    }

    const getVisibleGroups = (recordGroups = []) => {
      if (!Array.isArray(recordGroups)) {
        return []
      }

      return recordGroups.slice(0, getGroupsVisibleLimit(recordGroups))
    }

    const getHiddenGroups = (recordGroups = []) => {
      if (!Array.isArray(recordGroups)) {
        return []
      }

      return recordGroups.slice(getGroupsVisibleLimit(recordGroups))
    }

    const handleAction = (action, userObject = null) => {
      if (userObject) {
        mergeGroupsIntoList(userObject.groups)

        currentActionUser.value = {
          id: userObject.id,
          user_id: userObject.user_id ?? userObject.id,
          name: userObject.name,
          groups: userObject.groups ?? [],
          role: userObject.original_role ?? null,
        }
      } else {
        currentActionUser.value = {
          id: null,
          user_id: null,
          name: '',
          groups: [],
          role: null,
        }
      }

      currentAction.value = action
      showActionModal.value = true
    }

    const clearCurrentUserAndAction = () => {
      currentAction.value = ''
      actionToApply.value = null
      showActionModal.value = false

      currentActionUser.value = {
        id: null,
        user_id: null,
        name: '',
        groups: [],
        role: null,
      }
    }

    const openFilterModal = () => {
      showFilterModal.value = true
    }

    const updateFilters = () => {
      showMobileFilterModal.value = false

      currentPage.value = 1
      fetchUsers()
    }

    const resetFilters = () => {
      additionalFilters.groups = []
      additionalFilters.without_limits_only = false
      limitFilter.value = limitFilterData[0]

      store.dispatch('users/setUserFilters', {
        filters: {
          name: '',
          job_title: '',
          department: '',
          online_from: '',
          created_at: '',
          email: '',
          telephone: '',
          groups: [],
          without_group_only: false,
          without_limits_only: false,
          level_from: null,
          level_to: null,
        },
        filterString: null,
      })

      store.dispatch('users/clearShowBlocked')

      currentPage.value = 1
      fetchUsers()
    }

    const applyTopFilters = () => {
      additionalFilters.without_limits_only = limitFilter.value === 'Без лимитов'

      store.dispatch('users/setUserFilters', {
        filters: {
          ...filters.value,
          groups: additionalFilters.groups,
          without_group_only: false,
          without_limits_only: additionalFilters.without_limits_only,
        },
        filterString: filterString.value,
      })

      currentPage.value = 1
      fetchUsers()
    }

    const handleLimitFilter = () => {
      applyTopFilters()
    }

    const handleGroupSelect = () => {
      applyTopFilters()
    }

    const handleTableChange = (pagination, filter, sorter) => {
      sortState.value = {
        field: sorter?.field ?? null,
        order: sorter?.order ?? null,
      }

      fetchUsers()
    }

    const retrieveEntitiesPerPage = () => {
      const storageValue = localStorage.getItem('perPage')
      entitiesPerPage.value = storageValue ? Number(storageValue) : 10
    }

    const setEntitiesPerPage = (num) => {
      localStorage.setItem('perPage', num)
    }

    const handlePageChange = (current, perPage) => {
      if (entitiesPerPage.value !== perPage) {
        entitiesPerPage.value = perPage
        setEntitiesPerPage(entitiesPerPage.value)
      }

      currentPage.value = current
      fetchUsers()
    }

    const listState = computed(() => store.state.users.staffListState)

    onBeforeMount(() => {
      currentPage.value = listState.value.currentPage ?? 1

      sortState.value = {
        field: listState.value.sortField ?? null,
        order: listState.value.sortOrder ?? null,
      }

      store.dispatch('group/getGroups').then(() => {
        groups.value = normalizeGroups(allGroups.value)

        groupsLoading.value = false
      }).catch(() => {
        groupsLoading.value = false
      })

      retrieveEntitiesPerPage()
      fetchUsers()
    })

    watch(() => limitFilter.value, () => {
      handleLimitFilter()
    })

    watch(filters, () => {
      updateFilters()
    }, {
      deep: true,
      immediate: false,
    })

    const { isMobile } = useBreakpoints(767)

    const fetchAfterSubmit = () => {
      showActionModal.value = false
      clearCurrentUserAndAction()
      fetchUsers()
    }

    const tableShell = ref(null)

    watch(tableLoading, async (loading) => {
      if (!loading) {
        await tableShell.value?.rebindScroll()
        await updateTableYScroll()
      }
    })

    watch(userData, async () => {
      await tableShell.value?.rebindScroll()
      await updateTableYScroll()
    }, {
      deep: true,
    })

    watch([
      currentPage,
      entitiesPerPage,
      sortState,
    ], () => {
      store.commit('users/setStaffListState', {
        currentPage: currentPage.value,
        sortField: sortState.value.field,
        sortOrder: sortState.value.order,
      })
    }, {
      deep: true,
    })

    onBeforeRouteLeave((to) => {
      const isStillInStaff = to.path.startsWith('/staff');

      if (!isStillInStaff) {
        store.commit('users/clearUsers')
        store.commit('users/clearStaffListState')
        store.dispatch('users/clearUserFilters')
        store.dispatch('users/clearSpecialStaff')
      }
    })

    return {
      router,
      NotFoundIcon,

      currentPage,
      entitiesPerPage,

      columns,
      scrollX,

      limitFilterData,
      limitFilter,

      applyToAll,
      actionToApply,

      groups,
      filters,
      filterString,
      additionalFilters,
      groupsSelectOpen,

      selectedUsers,
      onSelectChange,

      resetFilters,
      addNewUser,

      tableLoading,
      groupsLoading,

      showFilterModal,
      openFilterModal,
      filtersPopoverOpen,

      userData,
      currentActionUser,
      currentActionModalUser,
      currentAction,

      handleAction,
      handlePageChange,
      handleTableChange,
      handleLimitFilter,
      handleGroupSelect,

      totalUsers,
      showMobileFilterModal,
      showActionModal,
      modalActionLoading,

      isMobile,

      clearCurrentUserAndAction,
      fetchUsers,
      hidePagination,
      showOrderForm,
      fetchAfterSubmit,

      tableShell,
      tableRef,
      tableScroll,
      getRight,

      getVisibleGroups,
      getHiddenGroups,
    }
  },
}
</script>

<style scoped lang="scss">
.card__with-action {
  margin-bottom: 0;
}

.view-container {
  padding-top: 0;
  padding-bottom: 0;
}

.filter-block {
  display: flex;
  column-gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  row-gap: 12px;
  align-items: center;
}

.search-tool-block {
  @media (max-width: $mobile-breakpoint) {
    width: 100%;
  }
}

.search-tool-block .ant-select {
  width: 350px;

  @media (max-width: $mobile-breakpoint) {
    min-width: 200px;
    width: 100%;
  }
}

.add-button {
  margin-left: auto;

  @media (max-width: $mobile-breakpoint) {
    width: 100%;

    button {
      width: 100%;
    }
  }
}

.action-select-wrapper {
  min-width: 180px;

  @media (max-width: $mobile-breakpoint) {
    width: 100%;

    .ant-select {
      width: 100%;
    }
  }
}

.extended-filter-block {
  display: none;

  @media (max-width: $mobile-breakpoint) {
    margin: 24px 20px;
    display: flex;
    column-gap: 25px;
    align-items: center;
    justify-content: space-between;
  }
}

.extended-filter-block h2 {
  font-size: 20px;
  margin-bottom: 0;
  font-weight: 600;
  line-height: 120%;
}

.extended-search-block-mobile {
  display: none;
  margin-right: 10px;

  @media (max-width: 768px) {
    display: block;
  }
}

.search-outline-block {
  position: relative;
}

.dot {
  position: absolute;
  top: -1px;
  left: 11px;
  height: 5px;
  width: 5px;
  background-color: var(--color-error);
  border-radius: 50%;
}

.staff-link {
  color: var(--main-green);
  cursor: pointer;
}

.staff-link:hover {
  color: var(--polar-green-3);
}

.change-role-text {
  color: var(--main-green);
  font-size: 14px;
  line-height: 1.35;
  cursor: pointer;
}

.staff-groups-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  max-width: 100%;
}

.staff-group-tag {
  display: inline-flex;
  align-items: center;
  height: 24px;
  max-width: none;
  margin: 0 !important;
  overflow: visible;
  white-space: nowrap;
  text-overflow: initial;
  vertical-align: middle;
}

.staff-group-tag--more {
  flex: 0 0 auto;
  cursor: pointer;
}

.staff-groups-popover {
  width: 200px;
  max-height: 260px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
}

.staff-groups-popover__item {
  color: var(--main-font);
  font-size: 13px;
  line-height: 1.35;
  word-break: break-word;
}

.no-data-wrapper {
  height: 50vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  padding: 24px;
  text-align: center;
}

.no-data-wrapper button {
  margin-top: 10px;
}

.no-data-wrapper button:hover {
  border-color: var(--main-green);
  color: var(--main-green);
}

.no-data-text {
  color: var(--main-font);
}

.action-buttons {
  @media (max-width: 768px) {
    .ant-row {
      flex-direction: column;
      row-gap: 10px;
    }

    .ant-row button {
      width: 100%;
    }
  }
}

.modal-text {
  font-size: 16px;
}

h2 {
  font-weight: 600;
  font-size: 16px;
}

:deep(.ant-table-wrapper .ant-table-thead > tr > th) {
  padding: 5px;
}

:deep(.ant-table-wrapper .ant-table-tbody > tr:not(.ant-table-measure-row) > td) {
  padding: 10px 5px !important;
}

:deep(.anticon-reload) {
  cursor: pointer;
}

:deep(.anticon-reload:hover) {
  color: var(--main-green) !important;
}

:deep(.ant-input-focused) {
  border-color: var(--main-green);
}

.ant-input:hover,
.ant-input:focus {
  border-color: var(--main-green);
}

.ant-input:focus,
.ant-select-focused .ant-select-selector {
  box-shadow: 0 0 0 2px var(--polar-green);
}

.ant-input-affix-wrapper:hover,
.ant-input-affix-wrapper-focused {
  border-color: var(--main-green);
}

.ant-input-affix-wrapper-focused {
  box-shadow: 0 0 0 2px var(--polar-green);
}

:deep(.ant-select-arrow) {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.45);
}

:deep(.ant-select-arrow .anticon) {
  font-size: 12px;
}

:deep(.ant-btn-text.ant-dropdown-trigger) {
  color: lightgrey;
  height: auto !important;
  padding: 0 !important;
}

:deep(.ant-btn-text.ant-dropdown-trigger.ant-dropdown-open) {
  color: var(--main-font);
}

:deep(.ant-table-wrapper .ant-table-tbody > tr.ant-table-row-selected > td) {
  background-color: var(--light-grey);
}

:deep(.ant-table-wrapper .ant-table-tbody > tr.ant-table-row-selected:hover > td) {
  background-color: var(--light-grey);
}

:deep(.ant-tag) {
  margin-bottom: 0;
}

:deep(.ant-divider-horizontal) {
  margin: 16px 0 !important;
}

:deep(.manage_access) {
  width: 800px !important;
}

@media (max-width: $mobile-breakpoint) {
  :deep(.ant-pagination .ant-pagination-total-text) {
    display: none !important;
  }
}

:deep(.ant-table-wrapper .ant-table-tbody>tr:not(.ant-table-measure-row)>td:has(.ant-dropdown-trigger)),
:deep(.ant-table-wrapper .ant-table-tbody>tr:not(.ant-table-measure-row)>td:has(.ant-checkbox-wrapper)) {
  padding: 10px 5px !important;
}

:deep(.groups-filter-select .ant-select-selector) {
  min-height: 32px !important;
  max-height: 32px !important;
  overflow: hidden !important;
  align-items: flex-start !important;
  padding-right: 32px !important;
}

:deep(.groups-filter-select .ant-select-selection-overflow) {
  max-height: 30px;
  overflow-y: auto;
  overflow-x: hidden;
  flex-wrap: wrap;
  padding-right: 4px;
}

:deep(.groups-filter-select .ant-select-selection-overflow::-webkit-scrollbar) {
  width: 4px;
}

:deep(.groups-filter-select .ant-select-selection-overflow::-webkit-scrollbar-thumb) {
  background: rgba(0, 0, 0, 0.18);
  border-radius: 4px;
}

:deep(.groups-filter-select .ant-select-selection-item) {
  max-width: 140px;
}

:deep(.admins-table .ant-table-header table colgroup col:nth-child(1)),
:deep(.admins-table .ant-table-body table colgroup col:nth-child(1)) {
  width: 30px !important;
  min-width: 30px !important;
  max-width: 30px !important;
}

:deep(.admins-table .ant-table-header table colgroup col:nth-child(2)),
:deep(.admins-table .ant-table-body table colgroup col:nth-child(2)) {
  width: 35px !important;
  min-width: 35px !important;
  max-width: 35px !important;
}

:deep(.admins-table .ant-table-header) {
  scrollbar-gutter: stable;
}

:deep(.admins-table .ant-table-body) {
  scrollbar-gutter: stable;
}
</style>
